import React from "react";
import { Dialog, Box, Typography, DialogContent, DialogActions, Button, CircularProgress, useMediaQuery, Divider } from "@mui/material";
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import { Atom } from "lucide-react";

import theme from "../../util/theme/theme";
import { useSettings } from "../../providers/settings-provider";
import useApi from "../../hooks/use-api";
import routes from "../../api/routes";
import buildApiRequestConfig from "../../api/url-builder";

interface SimulationDetailProps {
    open: boolean;
    simulationId?: string | null;
    onClose: () => void;
}

const truncate = (str: string, maxLength: number = 50) => {
    if (str.length <= maxLength) return str;
    return str.slice(0, maxLength) + '...';
}

const SimulationDetail: React.FC<SimulationDetailProps> = ({
    open,
    simulationId,
    onClose,
}) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const initialColumns = [
        { id: 'date', label: 'Date', width: 80, type: 'date' as const, inlineEdit: false },
        { id: 'amount', label: 'Amount', type: 'currency' as const, width: 80 },
        { id: 'description', label: 'Description', width: 250, type: 'string' as const },
    ];
    
    const [data, setData] = React.useState<any>(null);
    const [columns, setColumns] = React.useState(initialColumns);
    const [isActive, setIsActive] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [detailData, setDetailData] = React.useState<any>(null);
    const { formatDate, formatAmount } = useSettings();
    const rowVirtualizerInstanceRef = React.useRef<any>(null);
    const [fetchDetail, setFetchDetail] = useApi();

    React.useEffect(() => {
        if (!simulationId || !open) return;
        const { url, method } = buildApiRequestConfig(routes.simulation.fetchDetailById, [{ param: 'id', value: simulationId }]);
        setFetchDetail({ url, method });
    }, [simulationId, open]);

    React.useEffect(() => {
        if (fetchDetail.data) {
            setDetailData(fetchDetail.data);
            setIsLoading(false);
            setIsActive(fetchDetail.data.simulation?.isActive ?? false);
        }
        if (fetchDetail.isLoading) {
            setIsLoading(true);
        }
    }, [fetchDetail.data, fetchDetail.isLoading]);

    const onToggleStatus = () => {
        setIsActive(!isActive);
    };

    const tableColumns = React.useMemo<MRT_ColumnDef<any>[]>(
        () => [
            {
                accessorKey: 'date',
                header: 'Date',
                size: 100,
                Cell: ({ cell }) => formatDate(new Date(cell.getValue<string>())),
            },
            {
                accessorKey: 'amount',
                header: 'Amount',
                size: 100,
                Cell: ({ cell }) => formatAmount?.(cell.getValue<number>()),
            },
            {
                accessorKey: 'description',
                header: 'Description',
                size: 200,
            },
        ],
        [formatDate, formatAmount]
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth={isMobile ? "sm" : "md"} fullWidth>
            <DialogContent sx={{ position: 'relative', pt: 3, px: isMobile ? 2 : 3, pb: 0 }}>
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    background: 'var(--color-primary)',
                    py: 0.75,
                    pl: 5,
                    pr: 3,
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 600,
                    clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 15% 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    zIndex: 1,
                }}>
                    <Atom size={18} color="#fff"/> Simulated Data
                </Box>
                {isLoading ? (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={isMobile ? 150 : 200} gap={2} width="100%">
                        <CircularProgress size={40} />
                        <Typography variant="h6" color="text.secondary">
                            Loading simulation details...
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Box 
                            display="flex" 
                            width="100%"
                        >
                            <Box width={120}>
                                <Typography fontSize={isMobile ? 12 : 14} fontWeight={600} color="primary">
                                    SIMULATION
                                </Typography>
                            </Box>
                        </Box>
                        <Box 
                            display="flex" 
                            width="100%" 
                            flexDirection={isMobile ? "column" : "row"} 
                            sx={{ 
                                px: 0.5,
                                py: 0.75,
                            }}>
                            <Box width={120}>
                                <Typography fontSize={isMobile ? 10 : 12} color="var(--color-text-muted)">
                                    Simulation Date
                                </Typography>
                            </Box>
                            <Box width={250}>
                                <Typography fontSize={isMobile ? 10 : 12} color="var(--color-text-muted)">
                                    Description
                                </Typography>
                            </Box>
                            <Box width={120}>
                                <Typography fontSize={isMobile ? 10 : 12} color="var(--color-text-muted)">
                                    Amount
                                </Typography>
                            </Box>
                            <Box width={120}>
                                <Typography fontSize={isMobile ? 10 : 12} color="var(--color-text-muted)">
                                    Category
                                </Typography>
                            </Box>
                            <Box width={120}>
                                <Typography fontSize={isMobile ? 10 : 12} color="var(--color-text-muted)">
                                    Bank
                                </Typography>
                            </Box>
                        </Box>
                        <Box 
                            display="flex" 
                            width="100%" 
                            flexDirection={isMobile ? "column" : "row"} 
                            sx={{ 
                                border: '1px solid #d7d7d7',
                                px: 0.5,
                                py: 0.75,
                                borderRadius: 1,
                                background: 'var(--color-container-background)',
                            }}>
                            <Box width={120}>
                                <Typography fontSize={isMobile ? 10 : 12} fontWeight={600}>
                                    {detailData?.simulatedTransaction ? formatDate(new Date(detailData.simulatedTransaction.date)) : '—'}
                                </Typography>
                            </Box>
                            <Box width={250}>
                                <Typography fontSize={isMobile ? 10 : 12} fontWeight={600}>
                                    {truncate(detailData?.simulatedTransaction?.description ?? '', 30)}
                                </Typography>
                            </Box>
                            <Box width={120}>
                                <Typography fontSize={isMobile ? 10 : 12} fontWeight={600}>
                                    {detailData?.simulatedTransaction ? formatAmount?.(detailData.simulatedTransaction.amount) : '—'}
                                </Typography>
                            </Box>
                            <Box width={120}>
                                <Typography fontSize={isMobile ? 10 : 12} fontWeight={600}>
                                    {detailData?.simulatedTransaction?.category?.name ?? '—'}
                                </Typography>
                            </Box>
                            <Box width={120}>
                                <Typography fontSize={isMobile ? 10 : 12} fontWeight={600}>
                                    {detailData?.simulatedTransaction?.bankAccount?.accountName ?? '—'}
                                </Typography>
                            </Box>
                        </Box>
                    </>
                )}
            </DialogContent>
            {!isLoading && (
                <>
                    <Divider sx={{ my: 1 }}/>
                    <DialogContent sx={{ py: 0 }}>
                        <Box 
                            display="flex" 
                            width="100%"
                        >
                            <Box width={120}>
                                <Typography fontSize={isMobile ? 12 : 14} fontWeight={600} color="primary">
                                    RULE
                                </Typography>
                            </Box>
                        </Box>
                        <Box 
                            display="flex" 
                            width="100%" 
                            flexDirection={isMobile ? "column" : "row"} 
                            sx={{ 
                                border: '1px solid #d7d7d7',
                                px: 0.5,
                                py: 0.75,
                                borderRadius: 1,
                                background: 'var(--color-container-background)',
                            }}>
                            <Box width={200}>
                                <Typography fontSize={isMobile ? 10 : 12} fontWeight={600}>
                                    Simulation: {detailData?.simulation?.name ?? '—'}
                                </Typography>
                            </Box>
                            <Box width={120}>
                                <Typography fontSize={isMobile ? 10 : 12} fontWeight={600}>
                                    Mode: {detailData?.rule?.mode ? detailData.rule.mode.charAt(0).toUpperCase() + detailData.rule.mode.slice(1) : '—'}
                                </Typography>
                            </Box>
                            <Box width={detailData?.rule?.mode === 'move' ? 'auto' : 260}>
                                <Typography fontSize={isMobile ? 10 : 12} fontWeight={600}>
                                    {detailData?.rule?.details?.start && detailData?.rule?.details?.end 
                                        ? `Between: ${formatDate(new Date(detailData.rule.details.start))} to ${formatDate(new Date(detailData.rule.details.end))}`
                                        : detailData?.rule?.description ?? '—'
                                    }
                                </Typography>
                            </Box>
                            {detailData?.rule?.details?.defer && (
                                <Box width={200}>
                                    <Typography fontSize={isMobile ? 10 : 12} fontWeight={600}>
                                        Defer until: {formatDate(new Date(detailData.rule.details.defer))}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </DialogContent>
                    <Divider sx={{ my: 1 }}/>
                </>
            )}
            {!isLoading && 
                <DialogContent sx={{ px: isMobile ? 1 : 3, py: 1 }}>
                    <Typography fontSize={isMobile ? 12 : 14} fontWeight={600} color="primary">
                        ORIGINAL
                    </Typography>
                    <MaterialReactTable
                        columns={tableColumns}
                        data={detailData?.originalTransactions ?? []}
                        enableRowVirtualization
                        enableColumnVirtualization
                        rowVirtualizerInstanceRef={rowVirtualizerInstanceRef}
                        rowVirtualizerOptions={{ overscan: 10 }}
                        columnVirtualizerOptions={{ overscan: 2 }}
                        enablePagination={false}
                        enableTopToolbar={false}
                        enableBottomToolbar={false}
                        enableColumnActions={false}
                        initialState={{ density: 'compact' }}
                        muiTableContainerProps={{ sx: { height: isMobile ? 75 : 150, overflowY: 'auto' } }}
                        muiTablePaperProps={{ sx: { boxShadow: 'none' } }}
                        muiTableHeadCellProps={{ sx: { background: 'var(--color-container-background)' } }}
                    />
                </DialogContent>
            }
            <DialogActions sx={{ justifyContent: isMobile ? 'center' : 'space-between', alignItems: 'center', px: isMobile ? 1 : 3, pb: isMobile ? 1 : 2, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
                <Button 
                    onClick={onClose} 
                    variant="outlined"
                    sx={{
                        width: isMobile ? '100%' : 150,
                    }}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SimulationDetail;
